import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { MessageCircle, X, Send, Bot, User, RefreshCw, Phone, Mail } from "lucide-react";
import { useLanguage } from "@/hooks/use-language";

const i18n = {
  en: {
    title: "Property Assistant",
    welcome: "Hi! Ask me anything about your stay.",
    placeholder: "Type your question...",
    suggestions: [
      "What's the WiFi password?",
      "What time is checkout?",
      "How do I use the pool?",
    ],
    errorGeneric: "Sorry, I'm having trouble right now. Please try again.",
    errorNetwork: "Connection lost. Please check your internet and try again.",
    errorExhausted: "I'm unable to respond right now. Please contact the property directly for assistance:",
    retryButton: "Retry",
    callHost: "Call host",
    emailHost: "Email host",
    typingIndicator: "Typing",
    home: "Home",
    checkout: "Checkout",
  },
  es: {
    title: "Asistente de Propiedad",
    welcome: "Hola! Preguntame cualquier cosa sobre tu estancia.",
    placeholder: "Escribe tu pregunta...",
    suggestions: [
      "Cual es la clave del WiFi?",
      "A que hora es la salida?",
      "Como uso la piscina?",
    ],
    errorGeneric: "Lo siento, tengo problemas ahora. Por favor intenta de nuevo.",
    errorNetwork: "Conexion perdida. Revisa tu internet e intenta de nuevo.",
    errorExhausted: "No puedo responder en este momento. Por favor contacta directamente a la propiedad:",
    retryButton: "Reintentar",
    callHost: "Llamar al anfitrion",
    emailHost: "Correo al anfitrion",
    typingIndicator: "Escribiendo",
    home: "Inicio",
    checkout: "Salida",
  },
} as const;

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  isError?: boolean;
  isExhausted?: boolean;
}

interface GuestChatProps {
  slug: string;
  token: string;
  apartmentName: string;
  lang?: "en" | "es";
}

export default function GuestChat({ slug, token, apartmentName, lang: langProp }: GuestChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { lang: hookLang } = useLanguage();

  const lang = langProp || hookLang;
  const t = i18n[lang];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = useCallback(async (overrideMessage?: string) => {
    const trimmed = (overrideMessage || input).trim();
    if (!trimmed || isLoading) return;

    const userMsg: ChatMessage = { role: "user", content: trimmed };

    if (!overrideMessage) {
      setMessages(prev => [...prev, userMsg]);
      setInput("");
    }

    setIsLoading(true);
    setLastFailedMessage(null);

    try {
      const res = await fetch(`/api/g/${slug}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          message: trimmed,
          history: messages.filter(m => !m.isError && !m.isExhausted),
          lang,
        }),
      });

      if (!res.ok) throw new Error("Chat failed");
      const data = await res.json();
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
      setConsecutiveErrors(0);
      setLastFailedMessage(null);
    } catch (err: unknown) {
      const isNetworkError = err instanceof TypeError && (
        err.message.includes("Failed to fetch") ||
        err.message.includes("NetworkError") ||
        err.message.includes("Load failed")
      );

      const newErrorCount = consecutiveErrors + 1;
      setConsecutiveErrors(newErrorCount);
      setLastFailedMessage(trimmed);

      if (newErrorCount >= 3) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: t.errorExhausted,
          isError: true,
          isExhausted: true,
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: isNetworkError ? t.errorNetwork : t.errorGeneric,
          isError: true,
        }]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, slug, token, messages, lang, t, consecutiveErrors]);

  const handleRetry = useCallback(() => {
    if (!lastFailedMessage) return;
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last?.isError) return prev.slice(0, -1);
      return prev;
    });
    sendMessage(lastFailedMessage);
  }, [lastFailedMessage, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isExhausted = consecutiveErrors >= 3;

  return (
    <>
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 z-50 rounded-full w-14 h-14 shadow-lg shadow-primary/30"
          size="icon"
          data-testid="button-open-chat"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
      )}

      {isOpen && (
        <div className="fixed bottom-20 right-4 left-4 z-50 sm:left-auto sm:w-[380px]" data-testid="container-chat">
          <Card className="flex flex-col h-[420px] shadow-xl overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-t-md">
              <div className="flex items-center gap-2 min-w-0">
                <Bot className="w-5 h-5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate" data-testid="text-chat-title">
                    {t.title}
                  </p>
                  <p className="text-[10px] opacity-80 truncate">{apartmentName}</p>
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="text-primary-foreground no-default-hover-elevate no-default-active-elevate"
                onClick={() => setIsOpen(false)}
                data-testid="button-close-chat"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Bot className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground" data-testid="text-chat-welcome">
                    {t.welcome}
                  </p>
                  <div className="mt-4 space-y-2">
                    {t.suggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        className="block w-full text-left text-xs px-3 py-2 rounded-md border hover-elevate"
                        onClick={() => {
                          setInput(suggestion);
                          setTimeout(() => inputRef.current?.focus(), 50);
                        }}
                        data-testid={`button-suggestion-${suggestion.slice(0, 10)}`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, idx) => (
                <div key={idx}>
                  <div
                    className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    data-testid={`chat-message-${idx}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-md text-sm whitespace-pre-wrap ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : msg.isError
                            ? "bg-destructive/10 text-destructive"
                            : "bg-muted"
                      }`}
                    >
                      {msg.content}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {msg.isExhausted && (
                    <div className="ml-9 mt-2 flex flex-wrap items-center gap-2" data-testid="contact-options">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => window.location.href = "tel:"}
                        data-testid="button-call-host"
                      >
                        <Phone className="w-3 h-3 mr-1" />
                        {t.callHost}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => window.location.href = "mailto:"}
                        data-testid="button-email-host"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        {t.emailHost}
                      </Button>
                    </div>
                  )}

                  {msg.isError && !msg.isExhausted && lastFailedMessage && idx === messages.length - 1 && (
                    <div className="ml-9 mt-2" data-testid="retry-container">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={handleRetry}
                        data-testid="button-retry"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        {t.retryButton}
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-2 items-start" data-testid="chat-typing-indicator">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                  <div className="bg-muted px-3 py-2 rounded-md flex items-center gap-1.5">
                    <span className="flex gap-1" aria-label={t.typingIndicator}>
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t bg-background flex items-center gap-2">
              <Input
                ref={inputRef}
                placeholder={t.placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading || isExhausted}
                data-testid="input-chat-message"
              />
              <Button
                size="icon"
                onClick={() => sendMessage()}
                disabled={!input.trim() || isLoading || isExhausted}
                data-testid="button-send-message"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
